import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bookmark, Trash2, Plus, FileDown, Upload, FileText, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { apiClient } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH } from '../constants/testIds';

const DEST_OPTIONS = ['Caribbean', 'Mexico', 'Europe', 'Alaska'];
const PACKAGE_OPTIONS = [
    { id: 'bronze', label: 'Bronze · $799/pp' },
    { id: 'silver', label: 'Silver · $1,499/pp' },
    { id: 'gold', label: 'Gold · $2,499/pp' },
    { id: 'platinum', label: 'Platinum VIP · $4,999/pp' },
];

export default function DashboardPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [saved, setSaved] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadForm, setUploadForm] = useState({ reservation_id: '', label: '', file: null });
    const [uploadOpen, setUploadOpen] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [draft, setDraft] = useState({ label: '', destination: '', package_id: '', departure_window: '', guests: 2 });

    useEffect(() => {
        if (loading) return;
        if (!user) {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/dashboard';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            return;
        }
        apiClient.get('/reservations/me').then((r) => setTrips(r.data)).catch(() => {});
        apiClient.get('/saved-searches').then((r) => setSaved(r.data)).catch(() => {});
        apiClient.get('/documents').then((r) => setDocuments(r.data)).catch(() => {});
    }, [user, loading]);

    const saveSearch = async () => {
        if (!draft.label) { toast.error('Add a label for this trip.'); return; }
        try {
            const { data } = await apiClient.post('/saved-searches', { ...draft, guests: Number(draft.guests) || 1 });
            setSaved((s) => [data, ...s]);
            setOpen(false);
            setDraft({ label: '', destination: '', package_id: '', departure_window: '', guests: 2 });
            toast.success('Saved.');
        } catch (_) { toast.error('Could not save.'); }
    };

    const removeSearch = async (id) => {
        try {
            await apiClient.delete(`/saved-searches/${id}`);
            setSaved((s) => s.filter((x) => x.id !== id));
        } catch (_) { toast.error('Failed to delete.'); }
    };

    const bookFromSaved = () => {
        navigate('/');
        setTimeout(() => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' }), 200);
    };

    const submitUpload = async (e) => {
        e.preventDefault();
        if (!uploadForm.file) { toast.error('Pick a file to upload.'); return; }
        const fd = new FormData();
        fd.append('file', uploadForm.file);
        const params = new URLSearchParams();
        if (uploadForm.reservation_id) params.set('reservation_id', uploadForm.reservation_id);
        if (uploadForm.label) params.set('label', uploadForm.label);
        const qs = params.toString() ? `?${params.toString()}` : '';
        setUploading(true);
        try {
            const { data } = await apiClient.post(`/documents/upload${qs}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setDocuments((d) => [data, ...d]);
            setUploadOpen(false);
            setUploadForm({ reservation_id: '', label: '', file: null });
            toast.success('Document uploaded.');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const downloadDoc = async (doc) => {
        try {
            const res = await apiClient.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.original_filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch (_) {
            toast.error('Download failed.');
        }
    };

    const removeDocument = async (id) => {
        try {
            await apiClient.delete(`/documents/${id}`);
            setDocuments((d) => d.filter((x) => x.id !== id));
            toast.success('Document removed.');
        } catch (_) { toast.error('Could not remove.'); }
    };

    const downloadItinerary = async (trip) => {
        setDownloadingId(trip.id);
        try {
            const res = await apiClient.get(`/reservations/${trip.id}/itinerary`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cruise-vibes-${trip.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch (_) {
            toast.error('Could not generate itinerary PDF.');
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading || !user) return <div className="min-h-[50vh] flex items-center justify-center text-white/60">Loading…</div>;

    return (
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-14" data-testid={DASH.page}>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-aqua mb-2">My account</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
                        Hey, <span className="font-serif-italic font-medium text-brand-gold">{user.name.split(' ')[0]}</span>
                    </h1>
                </div>
                <Button
                    onClick={() => {
                        navigate('/');
                        setTimeout(() => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' }), 200);
                    }}
                    className="btn-sunset rounded-full h-11 px-6 uppercase tracking-widest font-semibold"
                >
                    Get a Quote
                </Button>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-10">
                <Card title="Email" value={user.email} icon="fa-envelope" />
                <Card title="Account type" value={user.role === 'admin' ? 'Admin' : 'Member'} icon="fa-id-badge" />
                <Card title="Trips" value={trips.length} icon="fa-suitcase-rolling" />
            </div>

            <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Saved searches</h2>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="dashboard-save-search" className="btn-aqua rounded-full h-9 px-4 text-xs uppercase tracking-widest font-semibold"><Plus size={14} className="mr-1" /> Save a trip</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-brand-surface border-white/10">
                        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Save a trip idea</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <Field label="Label *"><Input data-testid="saved-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Christine's birthday cruise" className="bg-transparent border-white/15" /></Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Destination">
                                    <Select value={draft.destination} onValueChange={(v) => setDraft({ ...draft, destination: v })}>
                                        <SelectTrigger className="bg-transparent border-white/15"><SelectValue placeholder="Pick" /></SelectTrigger>
                                        <SelectContent>{DEST_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Package">
                                    <Select value={draft.package_id} onValueChange={(v) => setDraft({ ...draft, package_id: v })}>
                                        <SelectTrigger className="bg-transparent border-white/15"><SelectValue placeholder="Pick" /></SelectTrigger>
                                        <SelectContent>{PACKAGE_OPTIONS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Window"><Input value={draft.departure_window} onChange={(e) => setDraft({ ...draft, departure_window: e.target.value })} placeholder="Summer 2026" className="bg-transparent border-white/15" /></Field>
                                <Field label="Guests"><Input type="number" min={1} value={draft.guests} onChange={(e) => setDraft({ ...draft, guests: e.target.value })} className="bg-transparent border-white/15" /></Field>
                            </div>
                        </div>
                        <DialogFooter><Button data-testid="saved-submit" onClick={saveSearch} className="btn-sunset rounded-full h-10 px-6 uppercase tracking-widest font-semibold">Save</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div data-testid="dashboard-saved-searches" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
                {saved.length === 0 ? (
                    <div className="col-span-full bg-brand-surface border border-dashed border-white/10 rounded-2xl p-10 text-center text-white/55">
                        No saved trips yet. Click &quot;Save a trip&quot; to bookmark a future cruise idea.
                    </div>
                ) : saved.map((s) => (
                    <div key={s.id} className="bg-brand-surface border border-white/5 rounded-2xl p-5 flex flex-col hover:border-brand-aqua/30 transition-colors">
                        <div className="flex items-start justify-between">
                            <Bookmark className="text-brand-gold" size={18} />
                            <button data-testid={`saved-remove-${s.id}`} onClick={() => removeSearch(s.id)} className="text-white/40 hover:text-brand-pink"><Trash2 size={14} /></button>
                        </div>
                        <h3 className="font-display text-lg uppercase tracking-tight mt-3">{s.label}</h3>
                        <div className="text-xs text-white/55 mt-1">{[s.destination, s.departure_window, s.guests && `${s.guests} guests`].filter(Boolean).join(' · ')}</div>
                        <Button onClick={() => bookFromSaved(s)} className="btn-aqua mt-4 rounded-full h-9 text-xs uppercase tracking-widest font-semibold">Request Quote</Button>
                    </div>
                ))}
            </div>

            <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-5">My trips</h2>
            <div data-testid={DASH.trips} className="bg-brand-surface border border-white/5 rounded-2xl overflow-hidden">
                {trips.length === 0 ? (
                    <div className="p-10 text-center text-white/55">
                        No trips yet. <button onClick={() => { navigate('/'); setTimeout(() => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' }), 200); }} className="text-brand-aqua hover:underline">Request a quote →</button>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="text-xs uppercase tracking-widest text-white/40">
                            <tr>
                                <th className="text-left px-5 py-3">Trip</th>
                                <th className="text-left px-5 py-3">Destination</th>
                                <th className="text-left px-5 py-3">Departure</th>
                                <th className="text-left px-5 py-3">Total</th>
                                <th className="text-left px-5 py-3">Status</th>
                                <th className="text-right px-5 py-3">Itinerary</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trips.map((t) => (
                                <tr key={t.id} className="border-t border-white/5">
                                    <td className="px-5 py-4 font-mono text-xs text-brand-aqua">{t.id}</td>
                                    <td className="px-5 py-4">{t.destination}</td>
                                    <td className="px-5 py-4 text-white/70">{t.departure_date}</td>
                                    <td className="px-5 py-4 text-brand-gold">${(t.total_amount || 0).toFixed(2)}</td>
                                    <td className="px-5 py-4">
                                        <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${t.status === 'confirmed' ? 'bg-brand-aqua/15 text-brand-aqua' : 'bg-white/5 text-white/60'}`}>{t.status.replace('_', ' ')}</span>
                                    </td>
                                    <td className="px-5 py-4 text-right">
                                        <button
                                            data-testid={`trip-itinerary-${t.id}`}
                                            onClick={() => downloadItinerary(t)}
                                            disabled={downloadingId === t.id}
                                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand-aqua hover:text-brand-gold transition-colors disabled:opacity-50"
                                        >
                                            <FileDown size={14} />
                                            {downloadingId === t.id ? 'Building…' : 'PDF'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* DOCUMENTS */}
            <div className="flex items-center justify-between mt-14 mb-5">
                <div>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Travel documents</h2>
                    <p className="text-xs text-white/50 mt-1">Passports, visas, vaccination cards — upload once, we keep them secure for every trip.</p>
                </div>
                <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="dashboard-upload-doc" className="btn-aqua rounded-full h-9 px-4 text-xs uppercase tracking-widest font-semibold">
                            <Upload size={14} className="mr-1.5" /> Upload document
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-brand-surface border-white/10">
                        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Upload a travel document</DialogTitle></DialogHeader>
                        <form onSubmit={submitUpload} className="space-y-4">
                            <Field label="Label (optional)">
                                <Input
                                    data-testid="upload-doc-label"
                                    placeholder="Passport · Sarah K."
                                    value={uploadForm.label}
                                    onChange={(e) => setUploadForm({ ...uploadForm, label: e.target.value })}
                                    className="bg-transparent border-white/15"
                                />
                            </Field>
                            <Field label="Attach to a trip (optional)">
                                <Select value={uploadForm.reservation_id} onValueChange={(v) => setUploadForm({ ...uploadForm, reservation_id: v })}>
                                    <SelectTrigger className="bg-transparent border-white/15" data-testid="upload-doc-reservation"><SelectValue placeholder="Not linked" /></SelectTrigger>
                                    <SelectContent>
                                        {trips.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.id} · {t.destination}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="File (JPG, PNG, WEBP or PDF · max 10MB)">
                                <Input
                                    data-testid="upload-doc-file"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,application/pdf"
                                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                                    className="bg-transparent border-white/15 cursor-pointer file:text-white/70 file:bg-transparent file:border-0"
                                />
                            </Field>
                            <DialogFooter>
                                <Button data-testid="upload-doc-submit" type="submit" disabled={uploading} className="btn-sunset rounded-full h-10 px-6 uppercase tracking-widest font-semibold">
                                    {uploading ? 'Uploading…' : 'Upload'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div data-testid="dashboard-documents" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.length === 0 ? (
                    <div className="col-span-full bg-brand-surface border border-dashed border-white/10 rounded-2xl p-10 text-center text-white/55">
                        No documents yet. Upload your passport, visa, or vaccination card to keep everything in one place.
                    </div>
                ) : documents.map((d) => (
                    <div key={d.id} data-testid={`doc-card-${d.id}`} className="bg-brand-surface border border-white/5 rounded-2xl p-5 flex flex-col hover:border-brand-aqua/30 transition-colors">
                        <div className="flex items-start justify-between">
                            <FileText className="text-brand-aqua" size={20} />
                            <button data-testid={`doc-remove-${d.id}`} onClick={() => removeDocument(d.id)} className="text-white/40 hover:text-brand-pink" aria-label="Remove document">
                                <X size={14} />
                            </button>
                        </div>
                        <h3 className="font-display text-base uppercase tracking-tight mt-3 truncate">{d.label || d.original_filename}</h3>
                        <div className="text-[11px] text-white/55 mt-1 truncate">{d.original_filename}</div>
                        <div className="text-[10px] text-white/40 mt-1">
                            {(d.size / 1024).toFixed(0)} KB · {d.content_type.split('/')[1].toUpperCase()}
                            {d.reservation_id && <> · <span className="text-brand-gold">{d.reservation_id}</span></>}
                        </div>
                        <Button data-testid={`doc-download-${d.id}`} onClick={() => downloadDoc(d)} className="mt-4 rounded-full h-9 text-xs uppercase tracking-widest font-semibold bg-white/5 text-white hover:bg-brand-aqua/20 hover:text-brand-aqua border border-white/10">
                            <FileDown size={14} className="mr-1.5" /> Download
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Card({ title, value, icon }) {
    return (
        <div className="bg-brand-surface border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/50">
                <i className={`fa-solid ${icon} text-brand-aqua`}></i>{title}
            </div>
            <div className="mt-3 font-display text-2xl font-bold">{value}</div>
        </div>
    );
}
function Field({ label, children }) {
    return (
        <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-white/50">{label}</Label>
            {children}
        </div>
    );
}
